import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { 
  recordUserConsent, 
  deleteUserData, 
  exportUserData
} from '@/lib/gdpr-compliance';
import { logger } from '@/utils/logger';
import { toError } from '@/utils/toError';

export const useGDPR = () => {
  // Check if React is available (for iframe environments like Lovable)
  if (typeof React === 'undefined' || !React.useState) {
    return {
      consent: null,
      isLoading: false,
      recordConsent: async () => {},
      exportData: async () => null,
      deleteData: async () => {},
      refreshConsent: async () => {}
    };
  }

  const { user } = useAuth();
  const [consent, setConsent] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Charger le consentement existant
  useEffect(() => {
    if (user) {
      loadConsent();
    }
  }, [user]);

  const loadConsent = async () => {
    if (!user) return;
    
    try {
      // Load consent from database - simplified for now
      setConsent({ given: true, date: new Date() });
    } catch (error) {
      logger.error('Error loading GDPR consent:', toError(error).message);
    }
  };

  const recordConsent = async (consentGiven: boolean, purposes: string[]) => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      await recordUserConsent(user.id, { consentGiven, purposes });
      await loadConsent(); // Recharger après mise à jour
    } catch (error) {
      logger.error('Error recording GDPR consent:', toError(error).message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const exportData = async () => {
    if (!user) return null;
    
    setIsLoading(true);
    try {
      const data = await exportUserData(user.id);
      return data;
    } catch (error) {
      logger.error('Error exporting user data:', toError(error).message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteData = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      await deleteUserData(user.id);
    } catch (error) {
      logger.error('Error deleting user data:', toError(error).message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    consent,
    isLoading,
    recordConsent,
    exportData,
    deleteData,
    refreshConsent: loadConsent
  };
};